import { useTranslation } from 'react-i18next';
import { resonanceChargeLabel } from '@peruselab/structure-editor/chemistry/elements';
import { changeDepiction } from '@peruselab/structure-editor/domain/drawingDocumentCore';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';

const DIRECTIONS = {
  right: { x: 1, y: 0 },
  left: { x: -1, y: 0 },
  above: { x: 0, y: -1 },
  below: { x: 0, y: 1 }
} as const;
export function AtomTextPanel({ doc }: { readonly doc: UseDrawingDocumentResult }) {
  const { t } = useTranslation();
  const atomId = doc.singleAtomId;
  const direction = atomId ? doc.document.depiction.atomLabels[atomId]?.hydrogenDirection : undefined;
  const directionKey =
    Object.entries(DIRECTIONS).find(([, p]) => p.x === direction?.x && p.y === direction?.y)?.[0] ??
    (direction ? 'custom' : 'auto');
  return (
    <section className="rail-section">
      <h2>{t('rightRail.atomLabels')}</h2>
      <p className="rail-hint">{t(atomId ? 'rightRail.atomLabelHint' : 'rightRail.selectAtom')}</p>
      <fieldset className="rail-fields" disabled={!atomId}>
        <label>
          <span>{t('rightRail.hydrogenPosition')}</span>
          <select
            value={directionKey}
            onChange={(event) => {
              if (!atomId) return;
              const value = event.target.value as keyof typeof DIRECTIONS | 'auto';
              if (value !== 'auto') doc.updateImplicitHydrogenDirection(atomId, DIRECTIONS[value]);
              else
                doc.commitDocument((current) => {
                  const { hydrogenDirection: _direction, ...label } = current.depiction.atomLabels[atomId] ?? {
                    hydrogenDisplay: 'auto' as const
                  };
                  return changeDepiction(current, { atomLabels: { ...current.depiction.atomLabels, [atomId]: label } });
                });
            }}
          >
            <option value="auto">{t('rightRail.auto')}</option>
            {directionKey === 'custom' ? (
              <option value="custom" disabled>
                {t('rightRail.custom')}
              </option>
            ) : null}
            {Object.keys(DIRECTIONS).map((key) => (
              <option key={key} value={key}>
                {t(`rightRail.${key}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t('rightRail.resonanceCharge')}</span>
          <input
            type="number"
            step="any"
            placeholder={t('rightRail.none')}
            value={doc.selectedResonanceCharge ?? ''}
            onChange={(event) =>
              doc.updateSelectedResonanceCharge(
                Number.isNaN(event.target.valueAsNumber) ? null : event.target.valueAsNumber
              )
            }
          />
        </label>
      </fieldset>
      {doc.selectedResonanceCharge !== undefined ? (
        <p className="rail-hint">{resonanceChargeLabel(doc.selectedResonanceCharge)}</p>
      ) : null}
      <p className="rail-hint">{t('rightRail.textScope')}</p>
    </section>
  );
}
